import { optionalField, without } from "../fields";
import { type AxialCoord, hexDistance, hexEquals } from "../hex";
import { rankBy } from "../pick";
import { findHex, isOwnGround, unitById } from "../run/board";
import { onCadence } from "../run/cadence";
import { allied, hostile, sameOwner } from "../run/sides";
import type { CourtRules } from "../types/rules";
import type { FactionId, Mark, Muster, RunState, Unit } from "../types/state";
import { disbandOrphans } from "../units/movement";
import { orderUnit, routeUnit } from "../units/routes";
import { replaceUnit } from "../units/unit";
import { marksOf } from "./general";
import { commandRefusal, spendWill } from "./king";

/**
 * Commanders and squads (ADR-0011 slice 3, A42): promote, demote, aim, the banners dealt to them, the
 * muster point, and the commanders' turn.
 */

/** The faction's commanders, in id order; each holds a pip of its King's Trust. */
export function commandersOf(state: RunState, faction: FactionId): Unit[] {
  return state.units.filter((unit) => unit.owner === faction && unit.commander);
}

/** The followers of a commander, in id order. */
export function squadMembers(state: RunState, commanderId: number): Unit[] {
  return state.units.filter((unit) => unit.squadOf === commanderId);
}

/** The promote verb's precondition: an own unit without a badge, and the court's leave at its hex. */
export function canPromote(state: RunState, faction: FactionId, unitId: number): boolean {
  const unit = unitById(state, unitId);
  return (
    state.status === "running" &&
    state.kings !== null &&
    unit !== undefined &&
    unit.owner === faction &&
    !unit.commander &&
    commandRefusal(state, faction, unit.coord, "promote") === undefined
  );
}

/**
 * The King pins the badge in person (ADR-0011 slice 3): a field command, `costs.promote` Will,
 * and the commander holds a pip of Trust while alive. It keeps its kind and stats and leaves
 * any squad it was in. Same object back when refused.
 */
export function promoteCommander(state: RunState, faction: FactionId, unitId: number): RunState {
  const unit = unitById(state, unitId);
  if (!unit || !canPromote(state, faction, unitId)) {
    return state;
  }
  const own = without(unit, "squadOf");
  return replaceUnit(spendWill(state, faction, "promote"), unit, { ...own, commander: true });
}

/**
 * The King takes the badge back in person, for no Will: the pip returns, the squad dissolves
 * and the unit pays with half its HP (rounded down, at least 1). Same object back when refused.
 */
export function demoteCommander(state: RunState, faction: FactionId, unitId: number): RunState {
  const unit = unitById(state, unitId);
  if (
    state.status !== "running" ||
    !unit ||
    !sameOwner(unit.owner, faction) ||
    !unit.commander ||
    commandRefusal(state, faction, unit.coord, "demote") !== undefined
  ) {
    return state;
  }
  const own = without(unit, "target");
  const demoted: Unit = { ...own, commander: false, hp: Math.max(1, Math.floor(unit.hp / 2)) };
  const next = replaceUnit(state, unit, demoted);
  return { ...next, units: disbandOrphans(next.units) };
}

/**
 * The King points a commander at a hex: a court command (kingdom-wide from the capital, else
 * with the commander in reach), `costs.aim` Will. The commander walks there on its own
 * cadence. The same target again is a no-op; same object back when refused or no road exists.
 */
export function aimCommander(
  state: RunState,
  faction: FactionId,
  unitId: number,
  to: AxialCoord,
): RunState {
  const unit = unitById(state, unitId);
  if (state.status !== "running" || !unit || !sameOwner(unit.owner, faction) || !unit.commander) {
    return state;
  }
  if (unit.target && hexEquals(unit.target, to)) {
    return state;
  }
  if (
    commandRefusal(state, faction, unit.coord, "aim") !== undefined ||
    !routeUnit(state, unit, to)
  ) {
    return state;
  }
  return replaceUnit(spendWill(state, faction, "aim"), unit, { ...unit, target: to });
}

/**
 * Own land within `radius` of `centre`, the centre first, then the nearest to the commander;
 * none once the commander stands inside the ring. The ring of a defend banner (`callRadius`) and
 * of a muster point (1).
 */
function ringGoals(
  state: RunState,
  faction: FactionId,
  unit: Unit,
  centre: AxialCoord,
  radius: number,
): AxialCoord[] {
  if (hexDistance(unit.coord, centre) <= radius) {
    return [];
  }
  const ring = state.hexes.filter(
    (hex) => isOwnGround(hex, faction) && hexDistance(hex.coord, centre) <= radius,
  );
  return rankBy(
    ring,
    (hex) => hexDistance(hex.coord, centre),
    (hex) => hexDistance(hex.coord, unit.coord),
  ).map((hex) => hex.coord);
}

/**
 * The deal (A39): the commanders the King has not aimed are dealt to his banners like cards.
 * The nearest to the first banner takes it (then the lower id), the nearest of the rest takes
 * the second, and so on round the banners until every commander has one.
 */
export function dealMarks(state: RunState, faction: FactionId): ReadonlyMap<number, Mark> {
  const marks = marksOf(state, faction);
  const dealt = new Map<number, Mark>();
  let waiting = commandersOf(state, faction).filter((commander) => !commander.target);
  for (let round = 0; marks.length > 0 && waiting.length > 0; round += 1) {
    const mark = marks[round % marks.length];
    if (!mark) {
      break;
    }
    const [nearest] = rankBy(
      waiting,
      (commander) => hexDistance(commander.coord, mark.target),
      (commander) => commander.id,
    );
    if (!nearest) {
      break;
    }
    dealt.set(nearest.id, mark);
    waiting = waiting.filter((commander) => commander.id !== nearest.id);
  }
  return dealt;
}

/**
 * One commander's turn (ADR-0011 slice 3): it fights, and nothing else. With neither a target
 * nor a banner it holds. It goes for the nearest enemy unit within `commanderSight` (then the
 * lower id), else for its target while that is not its faction's, else for the nearest foreign
 * hex in sight, the other faction's before neutral land. Under a defend banner it comes into
 * the ring around it and holds there. The squad follows in the move step. It spends no Will:
 * its orders go out as a delegate's.
 */
function commanderTurn(
  state: RunState,
  faction: FactionId,
  unitId: number,
  mark: Mark | undefined,
): RunState {
  const court = state.rules.court;
  const before = unitById(state, unitId);
  if (!court || !before?.commander || !sameOwner(before.owner, faction)) {
    return state;
  }
  // The muster is kept on the commander, so the staged state is what the orders start from.
  const muster = before.target ? undefined : musterFor(state, before, mark);
  const unit: Unit = { ...without(before, "muster"), ...optionalField("muster", muster) };
  const staged = sameMuster(before.muster, muster) ? state : replaceUnit(state, before, unit);
  const holdAt = muster && !muster.done ? mark?.from : undefined;
  return commanderOrders(staged, faction, unit, court, mark, holdAt);
}

function sameMuster(a: Muster | undefined, b: Muster | undefined): boolean {
  if (!a || !b) {
    return a === b;
  }
  return hexEquals(a.target, b.target) && a.sinceTick === b.sinceTick && a.done === b.done;
}

/**
 * A42: where a commander stands with the muster of the banner it was dealt. It reaches the
 * point (or a hex beside it), and strikes once its squad is whole or `musterTicks` have passed
 * there. A muster that is done stays done while the banner stands; none without a muster point.
 */
function musterFor(state: RunState, unit: Unit, mark: Mark | undefined): Muster | undefined {
  const court = state.rules.court;
  if (!court || !mark?.from || mark.kind !== "attack") {
    return undefined;
  }
  const kept = unit.muster && hexEquals(unit.muster.target, mark.target) ? unit.muster : undefined;
  if (kept?.done) {
    return kept;
  }
  if (hexDistance(unit.coord, mark.from) > 1) {
    return { target: mark.target, done: false };
  }
  const sinceTick = kept?.sinceTick ?? state.tick;
  const whole = squadMembers(state, unit.id).length >= court.squadSize;
  const done = whole || state.tick - sinceTick >= court.general.musterTicks;
  return { target: mark.target, sinceTick, done };
}

function commanderOrders(
  state: RunState,
  faction: FactionId,
  unit: Unit,
  court: CourtRules,
  mark: Mark | undefined,
  holdAt: AxialCoord | undefined,
): RunState {
  // The King's own aim comes before the banner the deal gave it (A39).
  const target = unit.target ?? mark?.target;
  if (!target) {
    return state;
  }
  const defending = unit.target === undefined && mark?.kind === "defend";
  const near = (coord: AxialCoord): boolean =>
    hexDistance(coord, unit.coord) <= court.commanderSight;
  const foes = rankBy(
    state.units.filter((other) => hostile(state.rules, other.owner, faction) && near(other.coord)),
    (other) => hexDistance(other.coord, unit.coord),
    (other) => other.id,
  ).map((other) => other.coord);
  const aimed = findHex(state, target);
  const marching =
    aimed !== undefined && !aimed.razed && !allied(state.rules, aimed.owner, faction);
  // In sight and not its own: the rival's land before neutral land, then the nearest.
  const inSight = (): AxialCoord[] =>
    rankBy(
      state.hexes.filter(
        (hex) => !hex.razed && !allied(state.rules, hex.owner, faction) && near(hex.coord),
      ),
      (hex) => (hex.owner === "none" ? 1 : 0),
      (hex) => hexDistance(hex.coord, unit.coord),
    ).map((hex) => hex.coord);
  const land = holdAt
    ? ringGoals(state, faction, unit, holdAt, 1)
    : defending
      ? ringGoals(state, faction, unit, target, court.general.callRadius)
      : marching
        ? [target]
        : inSight();
  for (const goal of [...foes, ...land]) {
    if (unit.destination && hexEquals(unit.destination, goal)) {
      return state;
    }
    const ordered = orderUnit(state, faction, unit.id, goal, "commander");
    if (ordered !== state) {
      return ordered;
    }
  }
  return state;
}

/** End of tick, on the commanders' cadence: every commander of both factions takes its turn, in id order. */
export function commandersAct(state: RunState): RunState {
  const court = state.rules.court;
  if (!court || !onCadence(state, court.commanderCadenceTicks)) {
    return state;
  }
  let next = state;
  for (const faction of state.factions) {
    const dealt = dealMarks(state, faction);
    for (const commander of commandersOf(state, faction)) {
      next = commanderTurn(next, faction, commander.id, dealt.get(commander.id));
    }
  }
  return next;
}
